import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
   useCallkitDataKeys,
   setHideNoVideo,
   setMaxTiles,
   setPinnedId,
   setViewMode,
   updateCallSwitch,
   setShowParticipantsPanel,
} from '../model';
import { computeLayout } from '../utils/layout';
import { toggleAudio, toggleVideo, endCall } from '../services/callService';
import { CALLKIT_CALLSTATUS, CALL_MODES, CALL_SWITCH_REQUEST_INIT, CALL_TYPE, CALLKIT_VIEWMODE } from '../constants';
import { isEnabled } from '../subFeatureRegistry';
import { fetchUsersInfo } from '@/features/users';
import { getLocalUserName, isOwnUserReconnecting } from '../utils/helper';
import { showWarningToast } from '@/shared/utils';
import { useLanguage } from '@/app/providers';
import { hidePopup } from '@/components/PopupContainer/popupService';

const EMPTY_OBJ = {};

/**
 * ViewModel for CallConnectedScreen.
 * Owns: participant mapping, layout computation, audio/video toggle, pin, view mode.
 */
export const useCallConnectedScreen = () => {
   const { t } = useLanguage();
   const dispatch = useDispatch();
   const lastAudioToggleTime = useRef(0);
   const lastVideoToggleTime = useRef(0);
   const TOGGLE_COOLDOWN = 1000;

   const {
      viewMode,
      maxTiles,
      hideNoVideo,
      pinnedId,
      activeSpeakerId,
      ongoingCallParticipants,
      callType,
      callMode,
      showParticipantsPanel,
   } = useCallkitDataKeys([
      'viewMode',
      'maxTiles',
      'hideNoVideo',
      'pinnedId',
      'activeSpeakerId',
      'ongoingCallParticipants',
      'callType',
      'callMode',
      'showParticipantsPanel',
   ]);

   const usersInfo = useSelector(state => state.userInfoData?.users || EMPTY_OBJ);

   // ── Map participants to UI format ───────────────────────────────────────────
   const mappedParticipants = useMemo(() => {
      const localUserId = getLocalUserName();
      const seen = new Set();

      const currentParticipants = (ongoingCallParticipants || []).filter(p => {
         // Deduplicate by userId
         if (seen.has(p.userId)) return false;
         seen.add(p.userId);
         // Always keep local user and screen share virtual tiles
         if (p.userId === localUserId || p.userId.endsWith('-screen')) return true;
         // Filter out remote participants that have already disconnected — they are
         // being removed by removeParticipantFromCall (setTimeout 0) and should not
         // flash as tiles on the connected screen
         return p.callStatus !== CALLKIT_CALLSTATUS.DISCONNECTED;
      });

      return currentParticipants.map(p => {
         let isAudioMuted = p.isAudioMuted;
         let isVideoMuted = p.isVideoMuted;

         if (
            p.callStatus === CALLKIT_CALLSTATUS.CALLING ||
            p.callStatus === CALLKIT_CALLSTATUS.RINGING ||
            p.callStatus === CALLKIT_CALLSTATUS.TRY_TO_CONNECT
         ) {
            isAudioMuted = true;
            isVideoMuted = true;
         }

         const isScreen = p.userId.endsWith('-screen');
         const baseId = isScreen ? p.userId.replace('-screen', '') : p.userId;
         const isLocal = baseId === getLocalUserName();
         const user = usersInfo[baseId];
         const baseName = user?.nickName || user?.name || p.name || baseId || p.userId;

         let name = baseName;
         if (isScreen) {
            name = isLocal ? 'You' : baseName;
         }

         return {
            ...p,
            userId: p.userId,
            id: p.userId,
            name: name || p.userId,
            isAudioMuted,
            isVideoMuted,
            stream: p.stream,
            isLocal: isLocal,
            isScreenSharing: isScreen,
         };
      });
   }, [ongoingCallParticipants, usersInfo]);

   // ── Fetch missing profiles ───────────────────────────────────────────────
   useEffect(() => {
      const missingIds = mappedParticipants.filter(p => !p.isScreenSharing && !usersInfo[p.id]).map(p => p.id);
      if (missingIds.length > 0) fetchUsersInfo(missingIds);
   }, [mappedParticipants, usersInfo]);

   // ── Local participant state ──────────────────────────────────────────────
   const localParticipant = mappedParticipants.find(p => p.isLocal && !p.isScreenSharing);
   const isAudioMuted = localParticipant?.isAudioMuted || false;
   const isVideoMuted = localParticipant?.isVideoMuted || false;

   // ── Handlers ─────────────────────────────────────────────────────────────
   const handleToggleAudio = useCallback(() => {
      if (isOwnUserReconnecting()) {
         showWarningToast(t('common.INTERNET_ERROR_MESSAGE'));
         return;
      }
      const now = Date.now();
      if (now - lastAudioToggleTime.current < TOGGLE_COOLDOWN) return;
      lastAudioToggleTime.current = now;

      toggleAudio(isAudioMuted);
   }, [isAudioMuted, t]);

   const handleToggleVideo = useCallback(() => {
      if (isOwnUserReconnecting()) {
         showWarningToast(t('common.INTERNET_ERROR_MESSAGE'));
         return;
      }
      const now = Date.now();
      if (now - lastVideoToggleTime.current < TOGGLE_COOLDOWN) return;
      lastVideoToggleTime.current = now;

      if (
         isEnabled('videoSwitchEnable') &&
         callType === CALL_TYPE.AUDIO &&
         isVideoMuted &&
         callMode === CALL_MODES.ONETOONE
      ) {
         dispatch(updateCallSwitch({ switchAction: CALL_SWITCH_REQUEST_INIT, fromUser: localParticipant?.id }));
         return;
      }
      toggleVideo(isVideoMuted);
   }, [callType, isVideoMuted, callMode, localParticipant, dispatch, t]);

   const handlePin = useCallback(id => dispatch(setPinnedId(id)), [dispatch]);
   const handleEndCall = useCallback(() => endCall(), []);
   const handleSetViewMode = useCallback(mode => dispatch(setViewMode(mode)), [dispatch]);
   const handleSetMaxTiles = useCallback(num => dispatch(setMaxTiles(num)), [dispatch]);
   const handleSetHideNoVideo = useCallback(bool => dispatch(setHideNoVideo(bool)), [dispatch]);
   const handleToggleParticipantsPanel = useCallback(() => {
      const nextState = !showParticipantsPanel;
      dispatch(setShowParticipantsPanel(nextState));
      if (nextState) {
         hidePopup('groupCallSelectionModal');
      }
   }, [dispatch, showParticipantsPanel]);

   // ── Auto Mute on Permission Revoked ───────────────────────────────────────
   useEffect(() => {
      if (typeof navigator?.permissions?.query !== 'function') return;

      const onVideoPermChange = function () {
         if (this.state === 'denied' && !isVideoMuted) toggleVideo(false);
      };
      const onVideoPermQuery = perm => {
         if (perm.state === 'denied' && !isVideoMuted) toggleVideo(false);
         perm.onchange = onVideoPermChange;
      };
      const onAudioPermChange = function () {
         if (this.state === 'denied' && !isAudioMuted) toggleAudio(false);
      };
      const onAudioPermQuery = perm => {
         if (perm.state === 'denied' && !isAudioMuted) toggleAudio(false);
         perm.onchange = onAudioPermChange;
      };
      navigator.permissions
         .query({ name: 'camera' })
         .then(onVideoPermQuery)
         .catch(() => {});
      navigator.permissions
         .query({ name: 'microphone' })
         .then(onAudioPermQuery)
         .catch(() => {});
   }, [isAudioMuted, isVideoMuted]);

   // ── Screen Sharing Detection ─────────────────────────────────────────────
   const screenShareParticipant = useMemo(() => {
      // Prioritize remote screen share over local for triggering the layout shift
      return (
         mappedParticipants.find(p => p.isScreenSharing && !p.isLocal) ||
         mappedParticipants.find(p => p.isScreenSharing)
      );
   }, [mappedParticipants]);

   const screenShareId = screenShareParticipant?.id || null;

   // ── Auto-snap to sidebar when screenshare starts ─────────────────────────
   // When a screenshare tile appears for the first time, snap the view to sidebar
   // so the big tile shows the screenshare. The user can still manually toggle back
   // to tiled at any point — the snap only fires on the screenshare → no-screenshare
   // transition, not on every render.
   const prevScreenShareRef = useRef(null);
   useEffect(() => {
      if (screenShareId && prevScreenShareRef.current !== screenShareId) {
         dispatch(setViewMode(CALLKIT_VIEWMODE.SIDEBAR));
      }
      prevScreenShareRef.current = screenShareId;
   }, [screenShareId, dispatch]);

   // ── Auto-switch between sidebar (2 people) and tiled (3+) ────────────────
   // Applies to every call type (group, meet, one-to-one invite flow). At exactly
   // 2 participants a tiled layout no longer makes sense — snap to sidebar, whether
   // the call started at 2 or arrived there from 3+ leaving. Once a 3rd participant
   // joins, snap back to tiled. Skipped while a pin or screenshare is active — those
   // already have their own, more specific reason to be on sidebar, and shouldn't be
   // fought. Only fires on the transition across the 2/3 boundary (or on mount when
   // already at 2, since the initial call setup always defaults to tiled regardless
   // of count), not on every render, so a manual view choice in between isn't fought.
   const realParticipantCount = mappedParticipants.filter(p => !p.isScreenSharing).length;
   const prevParticipantCountRef = useRef(null);
   useEffect(() => {
      const prevCount = prevParticipantCountRef.current;
      const hasSidebarOverride = !!pinnedId || !!screenShareId;

      if (!hasSidebarOverride) {
         if (prevCount !== 2 && realParticipantCount === 2) {
            dispatch(setViewMode(CALLKIT_VIEWMODE.SIDEBAR));
         } else if (prevCount !== null && prevCount < 3 && realParticipantCount >= 3) {
            dispatch(setViewMode(CALLKIT_VIEWMODE.TILED));
         }
      }
      prevParticipantCountRef.current = realParticipantCount;
   }, [realParticipantCount, pinnedId, screenShareId, dispatch]);

   // ── Effective view mode ──────────────────────────────────────────────────
   // Follow the stored Redux viewMode directly; pin→sidebar is already handled
   // by the setPinnedId reducer. The screenshare snap above updates Redux so
   // the stored value is always the current intent.
   const effectiveViewMode = (() => {
      if (viewMode !== CALLKIT_VIEWMODE.AUTO) return viewMode;
      if (mappedParticipants.length <= 1) return CALLKIT_VIEWMODE.SPOTLIGHT;
      if (pinnedId) return CALLKIT_VIEWMODE.SIDEBAR;
      return CALLKIT_VIEWMODE.TILED;
   })();

   // ── Visible + Sorted ───────────────────────────────────────────────────────
   const displayedParticipants = useMemo(() => {
      const visible = mappedParticipants.filter(p => !hideNoVideo || !p.isVideoMuted);

      const highPriority = [];
      const others = [];
      const localCamera = [];

      visible.forEach(p => {
         const isPinned = p.id === pinnedId;
         const isScreen = p.isScreenSharing;
         const isActiveRemote = p.id === activeSpeakerId && !p.isLocal;

         if (isPinned || isScreen || isActiveRemote) {
            highPriority.push(p);
         } else if (p.isLocal) {
            localCamera.push(p);
         } else {
            others.push(p);
         }
      });

      // Sort high priority items: ScreenSharing (Remote > Local) > Pinned > Active Speaker
      highPriority.sort((a, b) => {
         // Screenshare priority
         if (a.isScreenSharing && !b.isScreenSharing) return -1;
         if (!a.isScreenSharing && b.isScreenSharing) return 1;
         if (a.isScreenSharing && b.isScreenSharing) {
            if (!a.isLocal && b.isLocal) return -1;
            if (a.isLocal && !b.isLocal) return 1;
         }

         // Pinned priority
         if (a.id === pinnedId && b.id !== pinnedId) return -1;
         if (a.id !== pinnedId && b.id === pinnedId) return 1;

         return 0;
      });

      // Combine high priority and others
      const combined = [...highPriority, ...others];

      // Position local camera based on the view mode:
      // In Sidebar mode, "you" should be the last tile in the list.
      // In Tiled mode (>8 users), "you" should be at the 7th position (index 6).
      const isSidebar = effectiveViewMode === 'sidebar';
      const list = [...combined];
      localCamera.forEach(p => {
         const insertIndex = isSidebar ? list.length : Math.min(list.length, 6);
         list.splice(insertIndex, 0, p);
      });

      const max = maxTiles || 8;

      // Sidebar (Tile View list) renders all tiles with scrolling.
      // Grid View (Tiled mode) maintains the +N overflow tile concept.
      if (!isSidebar && list.length > max) {
         const showCount = max - 1;
         const remainingCount = list.length - showCount;
         const viewList = list.slice(0, showCount);
         viewList.push({
            id: 'overflow-tile',
            name: `+${remainingCount} Others`,
            isOverflow: true,
            isAudioMuted: true,
            isVideoMuted: true,
            remainingParticipants: list.slice(showCount),
         });
         return viewList;
      }
      return list;
   }, [mappedParticipants, hideNoVideo, screenShareId, pinnedId, activeSpeakerId, maxTiles, effectiveViewMode]);

   // ── Layout (Primary calculation) ─────────────────────────────────────────
   const activeSpeakerParticipant = mappedParticipants.find(p => p.id === activeSpeakerId);
   const isLocalActiveSpeaker = activeSpeakerParticipant?.isLocal;

   const { primary } = computeLayout(displayedParticipants, {
      screenShareId,
      spotlightId: isLocalActiveSpeaker ? null : activeSpeakerId,
      pinnedId,
   });

   const primaryForMode = (() => {
      if (effectiveViewMode === 'spotlight' || effectiveViewMode === 'sidebar')
         return primary || displayedParticipants[0] || null;
      return null;
   })();

   // ── Cleanup ─────────────────────────────────────────────────────────────
   useEffect(() => {
      return () => {
         hidePopup('groupCallSelectionModal');
         dispatch(setShowParticipantsPanel(false));
      };
   }, [dispatch]);

   return {
      allParticipants: mappedParticipants,
      displayedParticipants,
      primaryForMode,
      activeSpeakerId,
      pinnedId,
      screenShareId,
      isAudioMuted,
      isVideoMuted,
      viewMode,
      effectiveViewMode,
      maxTiles,
      hideNoVideo,
      handleToggleAudio,
      handleToggleVideo,
      handlePin,
      handleEndCall,
      handleSetViewMode,
      handleSetMaxTiles,
      handleSetHideNoVideo,
      showParticipantsPanel,
      handleToggleParticipantsPanel,
      isReconnecting: isOwnUserReconnecting(),
   };
};
